import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Space,
  Form,
  Input,
  Table,
  Tag,
  Modal,
  Grid,
} from 'antd';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  fetchTaskStatistics,
  fetchTaskList,
  fetchTaskTypes,
  fetchGpuActiveStatistics,
  setPagination,
} from '../../store/slices/taskSlice';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { MonitoringTask } from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Dayjs } from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

type TimeRangeType = '10m' | '30m' | '1h' | '12h' | '24h' | 'custom';
type PriorityType = 'all' | '1' | '2';

const TaskMonitoring: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { taskStatistics, statisticsLoading, gpuActiveStatistics, gpuActiveLoading, taskList, taskTypes, pagination, listLoading } = useSelector((state: RootState) => state.task);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);
  const screens = Grid.useBreakpoint();

  const [statisticsTimeRangeType, setStatisticsTimeRangeType] = useState<TimeRangeType>('10m');
  const [statisticsCustomTimeRange, setStatisticsCustomTimeRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [listTimeRangeType, setListTimeRangeType] = useState<TimeRangeType>('10m');
  const [listCustomTimeRange, setListCustomTimeRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [priorityType, setPriorityType] = useState<PriorityType>('all');
  const [form] = Form.useForm();
  const isMountedRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const prevPaginationRef = useRef({ page: pagination.page, size: pagination.size });
  const [isGpuModalVisible, setIsGpuModalVisible] = useState(false);
  const [selectedGpuList, setSelectedGpuList] = useState<{ taskTypeName: string; gpuIds: string[] } | null>(null);

  const statusOptions = [
    { label: '排队中', value: 'QUEUED' },
    { label: '处理中', value: 'PROCESSING' },
    { label: '已完成', value: 'COMPLETED' },
    { label: '失败', value: 'FAILED' },
    { label: '超时', value: 'TIMEOUT' },
  ];

  // 根据类型计算时间范围
  const getTimeRange = (
    type: TimeRangeType,
    range: [Dayjs, Dayjs] | null,
  ): { startTime: number; endTime: number } => {
    const endTime = Date.now();
    let startTime = endTime;

    if (type === 'custom' && range) {
      return {
        startTime: range[0].valueOf(),
        endTime: range[1].valueOf(),
      };
    }

    switch (type) {
      case '10m':
        startTime = endTime - 10 * 60 * 1000;
        break;
      case '30m':
        startTime = endTime - 30 * 60 * 1000;
        break;
      case '1h':
        startTime = endTime - 60 * 60 * 1000;
        break;
      case '12h':
        startTime = endTime - 12 * 60 * 60 * 1000;
        break;
      case '24h':
        startTime = endTime - 24 * 60 * 60 * 1000;
        break;
      default:
        startTime = endTime - 10 * 60 * 1000;
    }

    return { startTime, endTime };
  };

  const refreshData = () => {
    const { startTime, endTime } = getTimeRange(statisticsTimeRangeType, statisticsCustomTimeRange);
    const priority = priorityType === 'all' ? undefined : parseInt(priorityType);
    dispatch(fetchTaskStatistics({ startTime, endTime, priority }));
    dispatch(fetchGpuActiveStatistics({ startTime, endTime, priority }));
  };

  const fetchTaskListData = () => {
    const { startTime, endTime } = getTimeRange(listTimeRangeType, listCustomTimeRange);
    const values = form.getFieldsValue();
    dispatch(fetchTaskList({
      startTime,
      endTime,
      taskId: values.taskId || null,
      status: values.status || '',
      taskType: values.taskType || null,
      userId: values.userId || null,
      page: pagination.page,
      size: pagination.size,
    }));
  };

  // 自动刷新
  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: () => {
      refreshData();
      fetchTaskListData();
    },
  });

  // 只在组件挂载时执行一次
  useEffect(() => {
    refreshData();
    fetchTaskListData();
    dispatch(fetchTaskTypes()); // 获取任务类型列表
    // 使用 setTimeout 确保初始化完成后才允许其他 useEffect 触发
    setTimeout(() => {
      isMountedRef.current = true;
      isInitialLoadRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时执行一次
  }, []);

  // 时间范围变化时只刷新统计数据（不刷新任务列表）
  useEffect(() => {
    if (!isInitialLoadRef.current) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在时间范围变化时刷新统计
  }, [statisticsTimeRangeType, statisticsCustomTimeRange]);

  // 优先级变化时只刷新统计数据（不刷新任务列表）
  useEffect(() => {
    if (!isInitialLoadRef.current) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在优先级变化时刷新统计
  }, [priorityType]);

  useEffect(() => {
    if (!isInitialLoadRef.current) {
      fetchTaskListData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在列表时间范围变化时刷新列表
  }, [listTimeRangeType, listCustomTimeRange]);

  // 分页变化时只刷新列表（跳过初始加载）
  useEffect(() => {
    // 跳过初始加载和第一次接口返回时的 pagination 更新
    if (!isInitialLoadRef.current && isMountedRef.current) {
      // 检查分页是否真的变化了（避免重复调用）
      if (prevPaginationRef.current.page !== pagination.page || prevPaginationRef.current.size !== pagination.size) {
        prevPaginationRef.current = { page: pagination.page, size: pagination.size };
        fetchTaskListData();
      }
    } else {
      // 更新 prevPaginationRef 但不调用接口
      prevPaginationRef.current = { page: pagination.page, size: pagination.size };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在分页变化时刷新列表
  }, [pagination.page, pagination.size]);

  const handleStatisticsTimeRangeChange = (value: TimeRangeType) => {
    setStatisticsTimeRangeType(value);
    if (value !== 'custom') {
      setStatisticsCustomTimeRange(null);
    }
  };

  const handleStatisticsCustomTimeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setStatisticsCustomTimeRange([dates[0], dates[1]]);
    } else {
      setStatisticsCustomTimeRange(null);
    }
  };

  const handleListTimeRangeChange = (value: TimeRangeType) => {
    setListTimeRangeType(value);
    if (value !== 'custom') {
      setListCustomTimeRange(null);
    }
  };

  const handleListCustomTimeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setListCustomTimeRange([dates[0], dates[1]]);
    } else {
      setListCustomTimeRange(null);
    }
  };

  const getTimeRangeLabel = (type: TimeRangeType) => {
    const labels: Record<TimeRangeType, string> = {
      '10m': '近10分钟',
      '30m': '近30分钟',
      '1h': '近1小时',
      '12h': '近12小时',
      '24h': '近24小时',
      custom: '自定义时间',
    };
    return labels[type];
  };



  const handleSearch = () => {
    // 如果当前不在第1页，设置页码为1，让useEffect处理查询
    if (pagination.page !== 1) {
      dispatch(setPagination({ page: 1 }));
    } else {
      // 如果已经在第1页，直接查询
      fetchTaskListData();
    }
  };

  const handleReset = () => {
    form.resetFields();
    dispatch(setPagination({ page: 1, size: 10 }));
    fetchTaskListData();
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    dispatch(setPagination({
      page: pagination.current || 1,
      size: pagination.pageSize || 10,
    }));
  };

  const sortedTaskList = useMemo(
    () => [...taskList].sort((a, b) => b.createdAt - a.createdAt),
    [taskList]
  );

  const getStatusTag = (status: string) => {
    const statusMap = {
      QUEUED: { color: 'blue', text: '排队' },
      PROCESSING: { color: 'processing', text: '处理中' },
      COMPLETED: { color: 'success', text: '已完成' },
      FAILED: { color: 'error', text: '失败' },
      TIMEOUT: { color: 'warning', text: '超时' },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return (
      <Tag color={statusInfo.color}>
        {statusInfo.text}
      </Tag>
    );
  };

  const getPriorityTag = (priority: number) => {
    const priorityMap = {
      1: { color: 'blue', text: '普通' },
      2: { color: 'orange', text: 'VIP' },
    };
    const priorityInfo = priorityMap[priority as keyof typeof priorityMap] || { color: 'default', text: priority };
    return <Tag color={priorityInfo.color}>{priorityInfo.text}</Tag>;
  };

  const handleShowGpuList = (taskTypeName: string, gpuIds: string[]) => {
    setSelectedGpuList({ taskTypeName, gpuIds });
    setIsGpuModalVisible(true);
  };

  const columns: ColumnsType<MonitoringTask> = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: screens.xs ? 120 : 200,
      ellipsis: true,
      fixed: screens.xs ? 'left' : undefined,
    },
    {
      title: '显示ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 100,
      responsive: ['sm'] as any,
    },
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: screens.xs ? 100 : 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: screens.xs ? 80 : 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
      ellipsis: true,
      responsive: ['md'] as any,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: screens.xs ? 80 : 100,
      render: (priority: number) => getPriorityTag(priority),
      responsive: ['sm'] as any,
    },
    {
      title: 'GPU ID',
      dataIndex: 'gpuId',
      key: 'gpuId',
      width: 150,
      render: (gpuId: string) => gpuId || '-',
      responsive: ['lg'] as any,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: screens.xs ? 110 : 150,
      render: (timestamp: number) =>
        screens.xs
          ? new Date(timestamp).toLocaleDateString()
          : new Date(timestamp).toLocaleString(),
      sorter: true,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: screens.xs ? '0 0 12px 0' : 0 }}>任务监控</h1>
        <Space
          wrap
          style={{
            width: '100%',
            justifyContent: screens.xs ? 'flex-start' : 'flex-end',
            marginTop: screens.xs ? 12 : 0
          }}
        >
          <Select
            value={statisticsTimeRangeType}
            onChange={handleStatisticsTimeRangeChange}
            style={{ width: screens.xs ? '48%' : 150, minWidth: 120 }}
          >
            <Option value="10m">近10分钟</Option>
            <Option value="30m">近30分钟</Option>
            <Option value="1h">近1小时</Option>
            <Option value="12h">近12小时</Option>
            <Option value="24h">近24小时</Option>
            <Option value="custom">自定义时间</Option>
          </Select>
          <Select
            value={priorityType}
            onChange={setPriorityType}
            style={{ width: screens.xs ? '48%' : 120, minWidth: 100 }}
          >
            <Option value="all">全部优先级</Option>
            <Option value="1">普通</Option>
            <Option value="2">VIP</Option>
          </Select>
          {statisticsTimeRangeType === 'custom' && (
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              value={statisticsCustomTimeRange}
              onChange={handleStatisticsCustomTimeChange}
              placeholder={['开始时间', '结束时间']}
              style={{ width: screens.xs ? '100%' : 'auto' }}
            />
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshData}
            loading={statisticsLoading}
            style={{ width: screens.xs ? '100%' : 'auto' }}
          >
            {screens.xs ? '刷新' : '刷新'}
          </Button>
        </Space>
      </div>

      {/* 统计信息 */}
      <Card loading={statisticsLoading} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Statistic
              title="总任务数"
              value={taskStatistics?.totalTasks || 0}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Statistic
              title="排队任务"
              value={taskStatistics?.queuedTasks || 0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Statistic
              title="处理中"
              value={taskStatistics?.processingTasks || 0}
              valueStyle={{ color: '#722ed1' }}
              prefix={<PlayCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Statistic
              title="已完成"
              value={taskStatistics?.completedTasks || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Statistic
              title="失败任务"
              value={taskStatistics?.failedTasks || 0}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Statistic
              title="超时任务"
              value={taskStatistics?.timeoutTasks || 0}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* GPU活跃统计 */}
      <Card style={{ marginBottom: 16 }} loading={gpuActiveLoading}>
        <h3 style={{ marginBottom: 16 }}>活跃GPU统计</h3>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8} md={6}>
            <Statistic
              title="总活跃GPU数"
              value={gpuActiveStatistics?.totalActiveGpus || 0}
              valueStyle={{ color: '#1890ff', fontSize: screens.xs ? '24px' : '28px' }}
            />
          </Col>
          <Col xs={24} sm={16} md={18}>
            <div style={{ maxHeight: '200px', overflowY: 'auto', overflowX: screens.xs ? 'auto' : 'visible' }}>
              <Table
                dataSource={gpuActiveStatistics?.gpuByTaskType || []}
                pagination={false}
                size="small"
                scroll={{ x: screens.xs ? 600 : undefined }}
                columns={[
                  {
                    title: '任务类型',
                    dataIndex: 'taskTypeName',
                    key: 'taskTypeName',
                    width: screens.xs ? 150 : 200,
                    render: (name: string, record) => screens.xs ? name : `${name} (${record.taskType})`,
                  },
                  {
                    title: '活跃GPU',
                    dataIndex: 'activeGpuCount',
                    key: 'activeGpuCount',
                    width: screens.xs ? 80 : 120,
                    render: (count: number) => (
                      <Tag color="blue">{count}</Tag>
                    ),
                  },
                  {
                    title: '处理任务',
                    dataIndex: 'totalTasksProcessed',
                    key: 'totalTasksProcessed',
                    width: screens.xs ? 80 : 120,
                    render: (count: number) => (
                      <Tag color="green">{count}</Tag>
                    ),
                  },
                  {
                    title: 'GPU列表',
                    dataIndex: 'gpuIds',
                    key: 'gpuIds',
                    width: screens.xs ? 140 : undefined,
                    ellipsis: true,
                    render: (gpuIds: string[], record) => (
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {screens.xs ? (
                          <>
                            {gpuIds.length}个GPU
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, marginLeft: 4 }}
                              onClick={() => handleShowGpuList(record.taskTypeName, gpuIds)}
                            >
                              查看
                            </Button>
                          </>
                        ) : (
                          <>
                            {gpuIds.slice(0, 3).join(', ')}
                            {gpuIds.length > 3 && (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, marginLeft: 4 }}
                                onClick={() => handleShowGpuList(record.taskTypeName, gpuIds)}
                              >
                                +{gpuIds.length - 3}更多
                              </Button>
                            )}
                          </>
                        )}
                      </span>
                    ),
                  },
                ]}
                rowKey="taskType"
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* 任务搜索列表 */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: screens.xs ? '0 0 12px 0' : 0 }}>任务列表</h3>
          <Space
            wrap
            style={{
              width: '100%',
              justifyContent: screens.xs ? 'flex-start' : 'flex-end',
              marginTop: screens.xs ? 12 : 0
            }}
          >
            <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
              时间范围：{getTimeRangeLabel(listTimeRangeType)}
            </span>
            <Select
              value={listTimeRangeType}
              onChange={handleListTimeRangeChange}
              style={{ width: screens.xs ? '48%' : 150, minWidth: 120 }}
            >
              <Option value="10m">近10分钟</Option>
              <Option value="30m">近30分钟</Option>
              <Option value="1h">近1小时</Option>
              <Option value="12h">近12小时</Option>
              <Option value="24h">近24小时</Option>
              <Option value="custom">自定义时间</Option>
            </Select>
            {listTimeRangeType === 'custom' && (
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                value={listCustomTimeRange}
                onChange={handleListCustomTimeChange}
                placeholder={['开始时间', '结束时间']}
                style={{ width: screens.xs ? '100%' : 'auto' }}
              />
            )}
          </Space>
        </div>

        {/* 搜索表单 */}
        <Form
          form={form}
          layout={screens.xs ? 'vertical' : 'inline'}
          style={{ marginBottom: 16 }}
          onFinish={handleSearch}
        >
          <Form.Item name="taskId" style={{ marginBottom: 8, width: screens.xs ? '100%' : 'auto' }}>
            <Input
              placeholder="任务ID"
              style={{ width: screens.xs ? '100%' : 200 }}
            />
          </Form.Item>
          <Form.Item name="status" style={{ marginBottom: 8, width: screens.xs ? '100%' : 'auto' }}>
            <Select
              placeholder="任务状态"
              style={{ width: screens.xs ? '100%' : 120 }}
              allowClear
            >
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="taskType" style={{ marginBottom: 8, width: screens.xs ? '100%' : 'auto' }}>
            <Select
              placeholder="任务类型"
              style={{ width: screens.xs ? '100%' : 200 }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {taskTypes.map(taskType => (
                <Option key={taskType.type} value={taskType.type}>
                  {taskType.name} ({taskType.type})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="userId" style={{ marginBottom: 8, width: screens.xs ? '100%' : 'auto' }}>
            <Input
              placeholder="用户ID"
              style={{ width: screens.xs ? '100%' : 150 }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8, width: screens.xs ? '100%' : 'auto' }}>
            <Space style={{ width: screens.xs ? '100%' : 'auto', display: 'flex' }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
                style={{ flex: screens.xs ? 1 : 'none' }}
              >
                搜索
              </Button>
              <Button onClick={handleReset} style={{ flex: screens.xs ? 1 : 'none' }}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 任务表格 */}
        <Table
          columns={columns}
          dataSource={sortedTaskList}
          loading={listLoading}
          rowKey="taskId"
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            showSizeChanger: !screens.xs,
            showQuickJumper: !screens.xs,
            showTotal: screens.xs ? undefined : (total) => `共 ${total} 条记录`,
            simple: screens.xs,
            size: screens.xs ? 'small' : 'default',
            onChange: (page, size) => {
              dispatch(setPagination({ page, size: size || 10 }));
            },
          }}
          scroll={{ x: screens.xs ? 500 : 1200 }}
          onChange={handleTableChange}
          size={screens.xs ? 'small' : 'middle'}
        />
      </Card>

      {/* GPU列表弹窗 */}
      <Modal
        title={`${selectedGpuList?.taskTypeName || ''} - GPU列表`}
        open={isGpuModalVisible}
        onCancel={() => setIsGpuModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsGpuModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={screens.xs ? '100%' : 800}
        style={screens.xs ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={screens.xs ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <div style={{ maxHeight: screens.xs ? 'none' : '500px', overflowY: screens.xs ? 'visible' : 'auto', overflowX: 'hidden' }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            共 {selectedGpuList?.gpuIds.length || 0} 个GPU
          </p>
          <Row gutter={[8, 8]}>
            {selectedGpuList?.gpuIds.map((gpuId, index) => (
              <Col xs={12} sm={8} md={8} key={gpuId}>
                <Tag style={{ display: 'block', textAlign: 'center', wordBreak: 'break-all', fontSize: screens.xs ? '12px' : '14px' }}>
                  {index + 1}. {gpuId}
                </Tag>
              </Col>
            ))}
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default TaskMonitoring;
